import React, { useRef, useState, useContext } from 'react'
import { Modal, Button, Space, message } from 'antd'
import { DownloadOutlined, CopyOutlined, ShareAltOutlined, CloseOutlined } from '@ant-design/icons'
import html2canvas from 'html2canvas'
import ProfitProofCard from './index'
import { useWindowDimensions } from '../../hooks'
import { ThemeContext } from 'theme/ThemeProvider'
import styled, { createGlobalStyle } from 'styled-components'

const GlobalModalStyle = createGlobalStyle<{ isDarkMode: boolean }>`
	.profit-proof-modal {
		.ant-modal-content {
			background-color: ${props => !props.isDarkMode ? '#0f172a' : '#f8fafc'};
			border-radius: 8px;
		}

		.ant-modal-header {
			background-color: transparent;
			border-bottom: ${props => !props.isDarkMode ? '1px solid #334155' : '1px solid #e2e8f0'};
			padding: 16px 24px;
		}

		.ant-modal-title {
			background-color: transparent;
		}

		.ant-modal-footer {
			background-color: transparent;
			border-top: ${props => !props.isDarkMode ? '1px solid #334155' : '1px solid #e2e8f0'};
		}

		.ant-modal-close-x {
			color: ${props => !props.isDarkMode ? '#ffffff' : '#1e293b'};
		}
	}
`

interface ProfitProofModalProps {
	visible: boolean
	onClose: () => void
	data: {
		brandName: string
		brandUrl: string
		symbol: string
		longShort: 'L' | 'S'
		profitAmount: number
		entryPrice: number
		exitPrice?: number
		amount: number
		tradedAt?: string
		isPnl?: boolean
	}
}

const ProfitProofModal: React.FC<ProfitProofModalProps> = ({
	visible,
	onClose,
	data,
}) => {
	const cardRef = useRef<HTMLDivElement>(null)
	const [loading, setLoading] = useState(false)
	const { width } = useWindowDimensions()
	const isMobile = width < 768
	const { isDarkMode } = useContext(ThemeContext)

	const handleSaveImage = async () => {
		if (!cardRef.current) return

		setLoading(true)
		try {
			const canvas = await html2canvas(cardRef.current, {
				backgroundColor: null,
				scale: 1.5, // 적절한 해상도
				logging: false,
				useCORS: true,
			})

			// Canvas를 Blob으로 변환
			canvas.toBlob(async blob => {
				if (!blob) {
					message.error('이미지 생성에 실패했습니다')
					setLoading(false)
					return
				}

				// 브라우저 다운로드
				const url = URL.createObjectURL(blob)
				const link = document.createElement('a')
				link.href = url
				link.download = `profit_${data.symbol}_${Date.now()}.png`
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				URL.revokeObjectURL(url)

				message.success('이미지가 저장되었습니다')
				setLoading(false)
			}, 'image/png')
		} catch (error) {
			console.error('Image generation error:', error)
			message.error('이미지 생성에 실패했습니다')
			setLoading(false)
		}
	}

	const handleCopyImage = async () => {
		if (!cardRef.current) return

		setLoading(true)
		try {
			const canvas = await html2canvas(cardRef.current, {
				backgroundColor: null,
				scale: 1.5, // 적절한 해상도
				logging: false,
				useCORS: true,
			})

			canvas.toBlob(async blob => {
				if (!blob) {
					message.error('이미지 생성에 실패했습니다')
					setLoading(false)
					return
				}

				try {
					// 클립보드 API 지원 확인
					if (!navigator.clipboard || !window.ClipboardItem) {
						throw new Error('클립보드 API를 지원하지 않습니다')
					}

					// 클립보드에 이미지 쓰기
					const clipboardItem = new ClipboardItem({ 'image/png': blob })
					await navigator.clipboard.write([clipboardItem])

					message.success('클립보드에 복사되었습니다')
					setLoading(false)
				} catch (clipboardError: any) {
					console.error('Clipboard error:', clipboardError)

					// 권한 문제인 경우
					if (clipboardError.name === 'NotAllowedError') {
						message.error('클립보드 접근 권한이 필요합니다. 브라우저 설정을 확인하시거나 다시 시도해주세요.')
					} else {
						message.error('클립보드 복사에 실패했습니다. HTTPS 환경에서만 동작합니다.')
					}
					setLoading(false)
				}
			}, 'image/png')
		} catch (error) {
			console.error('Image generation error:', error)
			message.error('이미지 생성에 실패했습니다')
			setLoading(false)
		}
	}

	return (
		<>
			<GlobalModalStyle isDarkMode={isDarkMode} />
			<Modal
				title={
					<span style={{ color: !isDarkMode ? '#ffffff' : '#1e293b' }}>
						수익 인증하기
					</span>
				}
				open={visible}
				onCancel={onClose}
				width={520}
				wrapClassName="profit-proof-modal"
				closeIcon={
					<CloseOutlined style={{ color: !isDarkMode ? '#ffffff' : '#1e293b' }} />
				}
				footer={
				<Space>
					<Button onClick={onClose}>취소</Button>
					<Button
						type="default"
						icon={<CopyOutlined />}
						onClick={handleCopyImage}
						loading={loading}>
						{isMobile ? '복사' : '클립보드 복사'}
					</Button>
					<Button
						type="primary"
						icon={<DownloadOutlined />}
						onClick={handleSaveImage}
						loading={loading}>
						{isMobile ? '저장' : '이미지 저장'}
					</Button>
				</Space>
			}
			centered
			destroyOnClose
			bodyStyle={{
				backgroundColor: !isDarkMode ? '#0f172a' : '#f8fafc',
				padding: '24px',
			}}>
			<div
				style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					padding: '20px 0',
				}}>
				<ProfitProofCard ref={cardRef} {...data} />
			</div>
		</Modal>
		</>
	)
}

export default ProfitProofModal
